<template>
  <div class="welcome">
    <v-form
      v-model="valid"
      ref="form"
      @submit.prevent="navigateToNextStep"
      lazy-validation>
      <h1 class="welcome__title">Welcome to Your Account</h1>
      <div class="welcome__body">
        <v-stepper :steps="steps"/>
        <v-error
          v-if="!valid && !withTerms"
          title="Please correct field(s) below" />
        <router-view
          @on-validation-change="handleValidationChange"
          @on-form-change="handleFormChange"
          @submit="handleSubmit"
          :formValid="valid"/>
      </div>
      <div class="welcome__footer" v-bind:class="{ 'welcome__footer--with-terms': withTerms }">
        <div v-if="showLegalTextBelowContinueButton" class="differentLegalLayout">
          <button
            v-if="showContinueButton"
            :disabled="!valid"
            type="submit"
            class="btn__offers">
            {{buttonTextContinue}}
          </button>
          <div
            v-html="getDisplayFinePrint"
            class="offers__info"
            :class="truncatedLegalTextEnabled && isLegalTextTruncated ? 'truncatedText' : ''">
          </div>
          <h2
v-if="truncatedLegalTextEnabled && isLegalTextTruncated &&
            showLegalTextBelowContinueButton"
            @click="handleTruncateChange">
            <span>Show More ▼</span>
          </h2>
          <h2
v-if="truncatedLegalTextEnabled && !isLegalTextTruncated &&
            showLegalTextBelowContinueButton"
            @click="handleTruncateChange">
            <span>Show Less ▲</span>
          </h2>
          <div class="welcome__terms">
            By using our site, you agree to our
            <a href="https://www.homegauge.com/hg-terms.html" target="_blank">Terms of Service</a>
            and
            <a href="https://www.homegauge.com/privacy.html" target="_blank">Privacy Policy</a>.
          </div>
        </div>
        <div v-else>
          <div class="welcome__terms">
            By using our site, you agree to our
            <a href="https://www.homegauge.com/hg-terms.html" target="_blank">Terms of Service</a>
            and
            <a href="https://www.homegauge.com/privacy.html" target="_blank">Privacy Policy</a>.
          </div>
          <button
            v-if="showContinueButton"
            :disabled="!valid"
            type="submit"
            class="btn">
            {{buttonTextContinue}}
          </button>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import ErrorBlock from '@/components/Shared/ErrorBlock.vue';
import AccountClient from '@/utilities/account-client';
import OffersClient from '@/utilities/offers-client';
import IpClient from '@/utilities/ip-client';
import { mapGetters, mapState } from 'vuex';
import Jornaya from '@/utilities/jornaya';
import TrustedForm from '@/utilities/trusted-form';
import Stepper from '../Welcome/Stepper.vue';
import InspectionsRoute from '../../router/routes/inspections.route';
import BaseViewComponentVue from '../Shared/BaseViewComponent.vue';
import FF from '../../constants/feature-flags';

export default {
  components: {
    'v-stepper': Stepper,
    'v-error': ErrorBlock,
  },
  extends: BaseViewComponentVue,
  data() {
    return {
      valid: true,
      formData: {},
      clients: {},
      steps: [
        {
          id: 'about-account',
          path: '/welcome/about-account',
          label: 'Welcome',
          active: false,
        },
        {
          id: 'contact-info',
          path: '/welcome/contact-info',
          label: 'Contact info',
          active: false,
        },
        {
          id: 'offers',
          path: '/welcome/offers',
          label: 'Offers',
          active: false,
        },
      ],
      withTerms: false,
      isLegalTextTruncated: true,
      userIp: null,
      trustedForm: null,
    };
  },
  computed: {
    ...mapGetters('User', ['userAuthToken']),
    ...mapGetters('Folders', ['getTemplateKey', 'getButtonTextContinue', 'getDisplayFinePrint', 'getIsAgreementEmpty']),
    ...mapState('User', {
      user: state => state.currentUser,
    }),
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
    showOptionalField() {
      return !this.enabledFeatures['WEB-5298-Welcome-Flow-Hide-Optional-Fields'];
    },
    showLegalTextBelowContinueButton() {
      return this.enabledFeatures['HG-377-Move-Legal-Text'] && this.$route.path === this.steps[2].path;
    },
    truncatedLegalTextEnabled() {
      return this.enabledFeatures['HG-380-Truncate-Legal-Text'];
    },
    buttonTextContinue() {
      if (this.pathIsOffers && !this.getIsAgreementEmpty) {
        return this.getButtonTextContinue;
      }
      return 'Continue';
    },
    tCPALegalEnabled() {
      return this.enabledFeatures[FF.HG_1021_STATE_TCPA_WELCOME_FLOW];
    },
    isFridaTemplate() {
      return this.getTemplateKey === 'frida';
    },
    pathIsOffers() {
      return this.$route.path === this.steps[2].path;
    },
    showContinueButton() {
      if (this.pathIsOffers && !this.getIsAgreementEmpty) {
        return this.isFridaTemplate;
      }
      return true;
    },
  },
  methods: {
    async navigateToNextStep() {
      if (!this.$refs.form.validate()) {
        return;
      }

      const stepIndex = this.findActiveStepIndex();
      if (stepIndex === 1) {
        await this.onSubmitStepFirst();
      }
      if (stepIndex === 2) {
        await this.onSubmitStepSecond();
        return;
      }

      this.steps[stepIndex].active = false;
      this.steps[stepIndex + 1].active = true;
      this.$router.push({ path: this.steps[stepIndex + 1].path }).then();
    },
    async requestUserIp() {
      const userIp = await this.clients.ipClient.getIp();
      this.userIp = userIp;
      return this.userIp;
    },
    onSubmitStepFirst() {
      const { currentUser } = this.$store.state.User;
      const payload = {
        personId: currentUser.personId,
        familyName: this.formData.familyName,
        givenName: this.formData.givenName,
        [`${this.formData.phoneType.toLowerCase()}Phone`]: this.formData.phoneNumber,
        primaryEmail: this.formData.primaryEmail,
      };
      if (this.showOptionalField) {
        payload.primaryAddress = {
          address1: this.formData.address1,
          address2: this.formData.address2,
          city: this.formData.city,
          country: 'US',
          postalCode: this.formData.postalCode,
          state: this.formData.state,
        };
      }
      try {
        this.$store.dispatch('User/patchPerson', payload);
      } catch (error) {
        // Swallow error so modal will close
      }
      if (this.showOptionalField) {
        this.updateLegalDetails(currentUser.legacyId, this.formData);
      }
    },
    async onSubmitStepSecond() {
      const jornaya = Jornaya.getInstance();
      const selections = this.formData.agreement;
      const { currentUser } = this.$store.state.User;
      const existingPhoneNumber = currentUser.workPhone || currentUser.mobilePhone
      || currentUser.homePhone;
      if (selections) {
        const { postalCode, state } = this.$store.state.Inspection.inspections[0];
        const certificateUrl = this.trustedForm.getTrustedFormCertificateUrl();
        const updateAgreementsPayload = {
          certificateUrl,
          selections,
          location: 'welcome',
          postalCode,
          state,
          userIp: this.userIp,
          templateKey: this.getTemplateKey,
          jornayaUniversalLeadId: jornaya.readToken(),
        };
        if (this.tCPALegalEnabled && this.formData.tcpa_shown) {
          updateAgreementsPayload.tcpa_outcome = this.formData.tcpa_outcome;
        }
        try {
          await this.clients.offersClient.updateAgreements(updateAgreementsPayload);
          const { phoneType, phoneNumber } = this.formData;
          if (!existingPhoneNumber && phoneNumber !== '') {
            const phoneNumberPayload = {
              personId: currentUser.personId,
              [`${phoneType.toLowerCase()}Phone`]: phoneNumber,
            };
            this.$store.dispatch('User/patchPerson', phoneNumberPayload);
          }
          this.$router.push({ path: InspectionsRoute.path }).then();
        } catch (error) {
          // Swallow error so modal will close
        }
      } else {
        this.$router.push({ path: InspectionsRoute.path }).then();
      }
    },
    updateLegalDetails(legacyId, formData) {
      const payload = {
        customerLegacyId: legacyId,
        body: {
          expectedClosingDate: formData.date,
          firstTimeHomebuyer: formData.firstTime,
        },
      };
      try {
        this.$store.dispatch('Leads/updateLegacyCustomer', payload);
      } catch (error) {
        // Swallow error so modal will close
      }
    },
    findActiveStepIndex() {
      return this.steps.findIndex(step => step.path === this.$router.currentRoute.path);
    },
    setCurrentActiveStep() {
      const stepIndex = this.findActiveStepIndex();
      this.steps[stepIndex].active = true;
    },
    checkIfPageWithTerms(curRoute) {
      this.withTerms = curRoute.path === '/welcome/offers';
    },
    handleValidationChange(valid) {
      setTimeout(() => {
        this.valid = valid;
      });
    },
    handleFormChange(data) {
      this.formData = {
        ...this.formData,
        ...data,
      };
    },
    handleTruncateChange() {
      this.isLegalTextTruncated = !this.isLegalTextTruncated;
    },
    initializeClients() {
      this.clients.offersClient = new OffersClient(this.userAuthToken);
      this.clients.accountClient = new AccountClient(
        process.env.VUE_APP_ROOT_API,
        this.userAuthToken,
      );
      this.clients.ipClient = new IpClient('https://api.ipify.org/');
    },
    handleSubmit() {
      if (!this.$refs.form.validate()) {
        return;
      }
      this.onSubmitStepSecond();
    },
  },
  async mounted() {
    this.trustedForm = new TrustedForm();
    await this.initialHydration([
      this.setCurrentActiveStep(),
      this.checkIfPageWithTerms(this.$route),
      this.initializeClients(),
      this.requestUserIp(),
    ]);
  },
  watch: {
    $route(curRoute) {
      this.checkIfPageWithTerms(curRoute);

      if (!this.withTerms) {
        this.valid = true;
      }
      this.steps.forEach(step => {
        // eslint-disable-next-line no-param-reassign
        step.active = false;
      });
      this.setCurrentActiveStep();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/vars/theme';
@import '../../styles/vars/typography';

.welcome {
  $self: &;
  font-family: $typography-font-thirdly;
  padding: 60px 50px;
  max-width: 825px;
  width: 100%;
  border-radius: 4px;
  box-shadow: 0 8px 24px 0 rgba(0, 0, 0, 0.1);
  background-color: $theme-color-white;
  margin: 32px auto 32px;

  &__title {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 32px;
    margin: 0;
    font-weight: 500;
    letter-spacing: -0.32px;
    text-transform: initial;
  }

  &__footer {
    // border-top: 1px solid $theme-color-gray-light;
    // padding-top: 30px;

    &--with-terms {
      #{ $self }__terms {
        display: block;
        align-items: center;
      }

    }
  }

  &__terms {
    text-align: right;
    display: none;
    margin-bottom: 16px;

    a {
      color: $theme-color-primary;
    }
  }
}
.differentLegalLayout{
  display: flex;
  flex-direction: column;
  .offers{
    &__info{
      font-size: 13px;
      font-weight: 500;
      border-top: 1px solid $theme-color-gray-light;
      padding-top: 20px ;
    }
  }
  h2{
    text-align:center;
    border-bottom: 1px solid $theme-color-gray-light;
    line-height:0.1em;
    margin:20px 0 20px;
    span{
      font-size: 15px;
      font-weight: 500;
      background:#fff;
      padding:0 10px;
    }
  }
}
.truncatedText{
  overflow:hidden;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
}

.btn, .btn__offers {
  font-family: $typography-font-thirdly;
  background: $theme-color-primary;
  color: $theme-color-black;
  border: none;
  float: right;
  font-size: 13px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 10px 20px;
  letter-spacing: 1.56px;
  border-radius: 3px;
  cursor: pointer;
  transition: all .3s;
  opacity: 1;
  &:hover {
    opacity: .8;
  }
  &:disabled, &[disabled] {
    background: #efefef;
    cursor: not-allowed;
  }
}
.btn__offers{
  align-self: end;
  margin-bottom: 15px;
}

@media all and (max-width: 655px) {
  .welcome {
    padding: 40px 15px;
    box-shadow: none;
    margin-top: 0;
    margin-bottom: 0;

    &__footer {
      display: flex;
      flex-direction: column-reverse;
    }

    &__terms {
      margin-top: 32px;
      text-align: center;
    }
  }

  .btn {
    width: 100%;
  }
}
</style>
